import React, { useCallback } from 'react';
import Button, { ButtonGroup } from '@atlaskit/button';
import { ModalTitle, ModalHeader, ModalFooter } from '@atlaskit/modal-dialog';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MODAL,
	type UIAnalyticsEvent,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import messages from '../../messages.tsx';

type Props = {
	issueKey: string;
	issueId: string;
	workType: string;
	onUnlinkCancel: () => void;
	onUnlinkSubmit: (analyticsEvent: UIAnalyticsEvent) => void;
};

const ConfirmationDialog = ({
	issueKey,
	issueId,
	workType,
	onUnlinkCancel,
	onUnlinkSubmit,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const handleUnlinkSubmit = useCallback(
		(e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'unlinkConfirmation', {
				issueId,
			});
			onUnlinkSubmit(analyticsEvent);
		},
		[onUnlinkSubmit, issueId],
	);

	const handleUnlinkCancel = useCallback(
		(e: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'unlinkCancel', {
				issueId,
			});
			onUnlinkCancel();
		},
		[onUnlinkCancel, issueId],
	);

	return (
		<ContextualAnalyticsData sourceName="removeLinkedIssue" sourceType={MODAL}>
			<ModalDialog
				messageId="issue-field-link-relationship-inline-edit-full.ui.confirmation-dialog.modal-dialog.unlink-confirmation.modal-dialog"
				messageType="transactional"
				onClose={onUnlinkCancel}
			>
				<FireScreenAnalytics />
				<ModalHeader hasCloseButton>
					<ModalTitle appearance="warning">
						{formatMessage(messages.unlinkConfirmationModalTitle, {
							issueKey,
							workType: workType.toLowerCase(),
						})}
					</ModalTitle>
				</ModalHeader>
				<ModalFooter>
					<ButtonGroup>
						<Button appearance="subtle" onClick={handleUnlinkCancel}>
							{formatMessage(messages.unlinkConfirmationCancelButton)}
						</Button>
						<Button appearance="warning" onClick={handleUnlinkSubmit}>
							{formatMessage(messages.unlinkConfirmationConfirmButton)}
						</Button>
					</ButtonGroup>
				</ModalFooter>
			</ModalDialog>
		</ContextualAnalyticsData>
	);
};

export default ConfirmationDialog;
